import type {
  DigestedSchema,
  DigestedSimpleField,
  DigestedObjectFieldWithFields,
} from '@wix/wix-data-client-common-standalone'
import type { FieldSpec, NestedFieldSpec } from '@wix/wix-data-schema-types'
import { FieldType } from '@wix/wix-data-schema-types'
const { object } = FieldType

const isOfObjectTypeWithFields = (field: FieldSpec | NestedFieldSpec) =>
  field.type === object && field.fields

const ensureReferencedNestedFieldLabel = (
  field: DigestedObjectFieldWithFields,
) => ({
  ...field,
  label: field.referencingFieldLabel
    ? `${field.referencingFieldLabel} / ${field.label}`
    : field.label,
})

export const addNestedFieldsToSchema = (schema: DigestedSchema) => {
  const processedFields = schema.fields.flatMap(field => {
    if (isOfObjectTypeWithFields(field)) {
      const normalizedField = ensureReferencedNestedFieldLabel(
        field as DigestedObjectFieldWithFields,
      )
      return [
        normalizedField,
        ...digestNestedFields(schema, normalizedField, normalizedField.name),
      ]
    }
    return field
  })
  return { ...schema, fields: processedFields }
}

const digestNestedFields = (
  schema: DigestedSchema,
  parentField: DigestedObjectFieldWithFields,
  keyPath: string,
): DigestedSimpleField[] =>
  Object.keys(parentField.fields).flatMap(nestedKey => {
    const nestedKeyPath = keyPath + '.' + nestedKey
    const currentFieldLabel = `${parentField.displayName} / ${parentField.fields[nestedKey].displayName}`

    const nestedField = parentField.fields[nestedKey]

    const digestedField = {
      ...nestedField,
      isDeleted: parentField.isDeleted,
      referencingFieldLabel: parentField.referencingFieldLabel,
      name: nestedKeyPath,
      label: currentFieldLabel,
    }

    if (isOfObjectTypeWithFields(digestedField)) {
      return [
        digestedField as DigestedObjectFieldWithFields,
        ...digestNestedFields(
          schema,
          digestedField as DigestedObjectFieldWithFields,
          nestedKeyPath,
        ),
      ]
    } else {
      return {
        ...(digestedField as DigestedSimpleField),
        isNested: true,
      }
    }
  })
